import { Injectable, Inject, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { AnalyticsService } from './analytics.service';
import { LanguageService } from './language.service';

declare global {
    interface Window {
        cookieconsent: any;
        gtag: (...args: any[]) => void;
        dataLayer: any[];
    }
}

@Injectable({ providedIn: 'root' })
export class CookieConsentService {
    private initialized = false;
    private scriptLoaded = false;
    private analyticsService = inject(AnalyticsService);
    private languageService = inject(LanguageService);

    constructor(@Inject(PLATFORM_ID) private platformId: Object) { }

    //Inicia el servicio de cookies

    init(): void {
        if (isPlatformBrowser(this.platformId) && !this.initialized) {
            this.loadScript().then(() => {
                this.initializeCookieConsent();
                this.initialized = true;
            });
        }
    }

    // Carga el script de cookieconsent dinámicamente
    private loadScript(): Promise<void> {
        return new Promise((resolve, reject) => {
            if (this.scriptLoaded) {
                resolve();
                return;
            }

            const script = document.createElement('script');
            script.src = 'https://cdn.jsdelivr.net/npm/cookieconsent@3/build/cookieconsent.min.js';
            script.onload = () => {
                this.scriptLoaded = true;
                resolve();
            };
            script.onerror = (error) => {
                reject(error);
            };

            document.head.appendChild(script);
        });
    }

    // Inicializa la configuración de cookies
    private initializeCookieConsent(): void {
        if (window.cookieconsent) {
            window.cookieconsent.initialise({
                palette: {
                    popup: { background: "#008188ff" },
                    button: { background: "#00326A" }
                },
                theme: "edgeless",
                type: "opt-in",
                revokable: false,
                content: {
                    message: "Utilizamos cookies en nuestra web para ofrecerles la experiencia más relevante, recordando vuestras preferencias y visitas repetidas.",
                    dismiss: "Aceptar",
                    allow: "Permitir",
                    deny: "Rechazar",
                    link: "Saber más",
                    href: `/${this.languageService.getCurrentLanguage()}/cookies-policy`
                },
                onInitialise: (status: string) => {
                    if (status === window.cookieconsent.status.allow) {
                        this.grantAnalyticsConsent();
                    }
                },
                onStatusChange: (status: string) => {
                    if (status === window.cookieconsent.status.allow) {
                        this.grantAnalyticsConsent();
                    } else {
                        this.denyAnalyticsConsent();
                    }
                }
            });
        }
    }

    // Activa Google Analytics tras el consentimiento del usuario
    private grantAnalyticsConsent(): void {
        this.analyticsService.grantConsent();
    }

    // Deniega Google Analytics si el usuario rechaza las cookies
    private denyAnalyticsConsent(): void {
        this.analyticsService.denyConsent();
    }

    // Verifica si el usuario ha dado consentimiento para las cookies
    hasConsent(): boolean {
        if (isPlatformBrowser(this.platformId) && window.cookieconsent) {
            return window.cookieconsent.hasConsented();
        }
        return false;
    }

    // Revoca el consentimiento de cookies
    revokeConsent(): void {
        if (isPlatformBrowser(this.platformId) && window.cookieconsent) {
            window.cookieconsent.revokeChoice();
        }
    }
}